import React from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ContestActivitySplit } from '../../models/types';
import { formatPace } from './athleteActivityStats';

// Same limits the backend flags with: no runner holds under 3:00/km, and a km at
// 60% of the median pace usually means a vehicle took over for a stretch.
const FASTEST_HUMAN_SECONDS_PER_KM = 180;
const UNEVEN_SPLIT_RATIO = 0.6;
const MIN_JUDGED_SPLIT_KM = 0.5;

const secondsPerKm = (s: ContestActivitySplit) => (s.distance > 0 ? s.movingTime / s.distance : 0);

const median = (values: number[]) => {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
};

// Km-by-km pace with a speed bar; implausibly fast km are highlighted.
export const AthleteActivitySplits = ({ splits }: { splits: ContestActivitySplit[] }) => {
  const { t } = useTranslation('contests');
  const judged = splits.filter(s => s.distance >= MIN_JUDGED_SPLIT_KM).map(secondsPerKm).filter(v => v > 0);
  const medianPace = judged.length >= 3 ? median(judged) : 0;
  const fastest = judged.length > 0 ? Math.min(...judged) : 0;

  return (
    <Box role="table" sx={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>
      {splits.map((split, index) => {
        const pace = secondsPerKm(split);
        const judgedSplit = split.distance >= MIN_JUDGED_SPLIT_KM && pace > 0;
        const tooFast = judgedSplit && (pace < FASTEST_HUMAN_SECONDS_PER_KM || (medianPace > 0 && pace < medianPace * UNEVEN_SPLIT_RATIO));
        const elevation = split.elevationDifference;
        return (
          <Box key={index} role="row" sx={{ display: 'grid', gridTemplateColumns: '44px 52px 1fr 52px', gap: 1, alignItems: 'center', color: tooFast ? 'var(--danger)' : 'var(--fg)', fontWeight: tooFast ? 600 : 400 }}>
            <Box component="span" sx={{ color: tooFast ? 'inherit' : 'var(--muted)' }}>
              {split.distance >= 0.95 ? t('athlete.splits.km', { n: index + 1 }) : t('common:units.km', { value: split.distance.toFixed(2) })}
            </Box>
            <span>{pace > 0 ? formatPace(pace / 60) : '--'}</span>
            <Box sx={{ height: 6, borderRadius: '3px', backgroundColor: 'var(--muted-bg)', overflow: 'hidden' }}>
              <Box sx={{ height: '100%', width: pace > 0 && fastest > 0 ? `${(fastest / pace) * 100}%` : 0, backgroundColor: tooFast ? 'var(--danger)' : 'var(--bar)' }} />
            </Box>
            <Box component="span" sx={{ textAlign: 'right', color: 'var(--muted)' }}>
              {elevation == null ? '' : t('athlete.splits.elevation', { value: `${elevation > 0 ? '+' : ''}${Math.round(elevation)}` })}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};
